import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import { mapFormFieldToNullableBooleanValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IUpdateProperty } from "@rentiohq/shared-frontend/dist/reduxV2/property";
import * as propertyActions from "@rentiohq/shared-frontend/dist/reduxV2/property/property.actions";
import * as propertySelectors from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import {
  AMENITY_NAMES,
  createAmenityItem,
  IUpsertPropertyAmenityItem,
  prependEANPrefix,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import * as propertyAmenityApi from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.api";
import {
  EExtraInfoGroup,
  EField,
  IExtraData,
} from "./schema.editPropertyExtraInfo.types";

export const isPerformingSubmitActionSelector =
  (propertyId: number) => (state: IPartialRootState) =>
    propertySelectors.isUpdating(state, propertyId);

export const performingSubmitActionErrorSelector =
  (propertyId: number) => (state: IPartialRootState) =>
    propertySelectors.updateError(state, propertyId);

// Helper to build amenity items for upsert
const buildAmenityItems = (
  extraData: IExtraData,
  formData: any,
): IUpsertPropertyAmenityItem[] => {
  const { propertyAmenities, extraInfoGroup } = extraData;
  const items: (IUpsertPropertyAmenityItem | null)[] = [];

  switch (extraInfoGroup) {
    case EExtraInfoGroup.Exterior:
      items.push(
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingAdjoined,
          formData[EField.BuildingAdjoined],
          "string",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingSurfaceArea,
          formData[EField.BuildingSurfaceArea],
          "number",
        ),
      );
      break;

    case EExtraInfoGroup.Interior:
      items.push(
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingFloor,
          formData[EField.BuildingFloor],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingFloorCount,
          formData[EField.BuildingFloorCount],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountBedroom,
          formData[EField.RoomCountBedroom],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountBathroom,
          formData[EField.RoomCountBathroom],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountGarage,
          formData[EField.RoomCountGarage],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingHasStorage,
          mapFormFieldToNullableBooleanValue(
            formData[EField.BuildingHasStorage],
          ),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.isFurnished,
          mapFormFieldToNullableBooleanValue(formData[EField.IsFurnished]),
          "boolean",
        ),
      );
      break;

    case EExtraInfoGroup.Amenities:
      items.push(
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasOven,
          mapFormFieldToNullableBooleanValue(formData[EField.HasOven]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasFridge,
          mapFormFieldToNullableBooleanValue(formData[EField.HasFridge]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasDishwasher,
          mapFormFieldToNullableBooleanValue(formData[EField.HasDishWasher]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasRangeHood,
          mapFormFieldToNullableBooleanValue(formData[EField.HasRangeHood]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasLift,
          mapFormFieldToNullableBooleanValue(formData[EField.HasLift]),
          "boolean",
        ),
      );
      break;

    case EExtraInfoGroup.Energy:
      items.push(
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasDoubleGlazing,
          mapFormFieldToNullableBooleanValue(formData[EField.HasDoubleGlazing]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasCentralHeating,
          mapFormFieldToNullableBooleanValue(
            formData[EField.HasCentralHeating],
          ),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.heatingType,
          formData[EField.HeatingType],
          "string",
        ),
      );
      break;
  }

  return items.filter(
    (item): item is IUpsertPropertyAmenityItem => item !== null,
  );
};

// Check if this extraInfoGroup should use propertyAmenity
const shouldUsePropertyAmenity = (extraInfoGroup: EExtraInfoGroup): boolean => {
  return [
    EExtraInfoGroup.Exterior,
    EExtraInfoGroup.Interior,
    EExtraInfoGroup.Amenities,
    EExtraInfoGroup.Energy,
  ].includes(extraInfoGroup);
};

export const submitActionCreator =
  (extraData: IExtraData) => (formData: any) => {
    const { property } = extraData;

    // For Exterior, Interior, Amenities, Energy - upsert to propertyAmenity
    if (shouldUsePropertyAmenity(extraData.extraInfoGroup)) {
      // Only send the amenities from this section (changed values only)
      const newAmenityItems = buildAmenityItems(extraData, formData);

      // For Exterior, we also need to update property for garden/terrace surface areas
      if (extraData.extraInfoGroup === EExtraInfoGroup.Exterior) {
        const gardenSurfaceArea =
          formData[EField.BuildingGardenSurfaceArea] ?? null;
        const terraceSurfaceArea =
          formData[EField.BuildingTerraceSurfaceArea] ?? null;

        // Check if we have actual property field changes (compare with current property values)
        const hasPropertyChanges =
          gardenSurfaceArea !== property.buildingGardenSurfaceArea ||
          terraceSurfaceArea !== property.buildingTerraceSurfaceArea;

        // Update all amenities including building surface area
        if (newAmenityItems.length > 0) {
          propertyAmenityApi.upsertForProperty(property.id, {
            amenities: newAmenityItems,
          });
        }

        // Only update property if we have actual property field changes
        if (hasPropertyChanges) {
          const updateProperty: IUpdateProperty = {
            buildingGardenSurfaceArea: gardenSurfaceArea,
            buildingTerraceSurfaceArea: terraceSurfaceArea,
          };

          return propertyActions.updateStart.getAction({
            id: property.id,
            data: updateProperty,
          });
        }

        // If no property changes, return a no-op action to trigger success callback
        return propertyActions.updateStart.getAction({
          id: property.id,
          data: {},
        });
      }

      // For other amenity-based groups (Interior, Amenities, Energy), only update amenities
      if (newAmenityItems.length > 0) {
        propertyAmenityApi.upsertForProperty(property.id, {
          amenities: newAmenityItems,
        });
      }

      // Return a no-op property action to trigger success callback without API call
      return propertyActions.updateStart.getAction({
        id: property.id,
        data: {},
      });
    }

    // For other groups, continue using property update
    const updateProperty: IUpdateProperty = {};

    switch (extraData.extraInfoGroup) {
      case EExtraInfoGroup.General:
        updateProperty.constructionYear =
          formData[EField.ConstructionYear] ?? null;
        break;

      case EExtraInfoGroup.Epc:
        updateProperty.epcLabel = formData[EEpcField.Label] ?? null;
        updateProperty.epcValue = formData[EEpcField.Value] ?? null;
        updateProperty.epcExpirationDate =
          formData[EEpcField.ExpirationDate] ?? null;
        updateProperty.epcCertificateId =
          formData[EEpcField.CertificateId] ?? null;
        break;

      case EExtraInfoGroup.Bookkeeping:
        updateProperty.costUnit = formData[EField.CostUnit] ?? null;
        updateProperty.bookkeepingPaidOutStartDate =
          formData[EField.BookkeepingPaidOutStartDate] ?? null;
        break;

      case EExtraInfoGroup.BookkeepingGroup:
        updateProperty.costCenter = formData[EField.CostCenter] ?? null;
        break;

      case EExtraInfoGroup.Electricity:
        updateProperty.electricityType =
          formData[EField.ElectricityType] ?? null;
        updateProperty.electricityEan =
          prependEANPrefix(formData[EField.ElectricityEan]) ?? null;
        updateProperty.electricityMeterNumberBoth =
          formData[EField.ElectricityMeterNumberBoth] ?? null;
        updateProperty.electricityMeterType =
          formData[EField.ElectricityMeterType] ?? null;
        break;

      case EExtraInfoGroup.NaturalGas:
        updateProperty.naturalGasAvailable =
          formData[EField.NaturalGasAvailable] ?? null;
        updateProperty.naturalGasEan =
          prependEANPrefix(formData[EField.NaturalGasEan]) ?? null;
        updateProperty.naturalGasMeterNumber =
          formData[EField.NaturalGasMeterNumber] ?? null;
        updateProperty.naturalGasMeterType =
          formData[EField.NaturalGasMeterType] ?? null;
        break;

      case EExtraInfoGroup.Water:
        updateProperty.waterMeterNumber =
          formData[EField.WaterMeterNumber] ?? null;
        break;

      default:
        break;
    }

    return propertyActions.updateStart.getAction({
      id: property.id,
      data: updateProperty,
    });
  };
